import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, Globe, Monitor, Smartphone, Tablet, Clock, MapPin, 
  Eye, TrendingUp, RefreshCw, Lock, ExternalLink, Chrome,
  Activity, Calendar, BarChart3, FileText, Plus, Trash2, 
  ToggleLeft, ToggleRight, Edit3, LogOut, BookOpen, CheckSquare,
  Square, RotateCcw, Search, LinkIcon, X, Download, Copy, FileCode, Upload
} from 'lucide-react';
import api from '../lib/api';

// Simple bar chart component
const SimpleBarChart = ({ data, labelKey, valueKey, maxBars = 10, color = '#C8A860' }) => {
  if (!data || data.length === 0) return <p style={{ color: '#888', fontSize: '0.9rem' }}>No data yet</p>;
  
  const maxValue = Math.max(...data.map(d => d[valueKey]));
  const displayData = data.slice(0, maxBars);
  
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {displayData.map((item, index) => (
        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '100px', fontSize: '0.8rem', color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item[labelKey] || 'Unknown'}
          </div>
          <div style={{ flex: 1, background: '#f0f0f0', borderRadius: '4px', height: '20px', position: 'relative' }}>
            <div style={{
              width: `${(item[valueKey] / maxValue) * 100}%`,
              background: color,
              height: '100%',
              borderRadius: '4px',
              minWidth: '20px'
            }} />
            <span style={{
              position: 'absolute',
              right: '6px',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '0.75rem',
              fontWeight: '600',
              color: '#333'
            }}>
              {item[valueKey]}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

// Stat card
const StatCard = ({ icon: Icon, label, value, color = '#1A1A1A' }) => (
  <div style={{
    background: 'white',
    padding: '20px',
    borderRadius: '10px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #e8e8e8'
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
      <Icon size={20} color={color} />
      <span style={{ fontSize: '0.85rem', color: '#666' }}>{label}</span>
    </div>
    <div style={{ fontSize: '2rem', fontWeight: '700', color: color }}>
      {value}
    </div>
  </div>
);

// Format referrer
const formatReferrer = (ref) => {
  if (!ref || ref === 'Direct' || ref === '') return 'Direct';
  if (ref.includes('google')) return 'Google';
  if (ref.includes('facebook')) return 'Facebook';
  if (ref.length > 25) return ref.substring(0, 25) + '...';
  return ref;
};

export const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('analytics');
  
  // Analytics state
  const [summary, setSummary] = useState(null);
  const [liveStats, setLiveStats] = useState(null);
  const [visitors, setVisitors] = useState([]);
  const [visitorFilter, setVisitorFilter] = useState('all'); // 'all' or 'new'
  
  // Notification settings state
  const [notificationSettings, setNotificationSettings] = useState({
    instantNotifications: true,
    dailySummary: false,
    summaryTime: '18:00'
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState('');
  
  // Blog state
  const [blogPosts, setBlogPosts] = useState([]);
  const [showNewPost, setShowNewPost] = useState(false);
  const [newPost, setNewPost] = useState({ title: '', summary: '', html_content: '', tags: '', thumbnail_url: '' });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [editingPost, setEditingPost] = useState(null);
  const [viewingPost, setViewingPost] = useState(null);

  // Resources state
  const [guides, setGuides] = useState([]);
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [guideContent, setGuideContent] = useState('');
  const [loadingGuide, setLoadingGuide] = useState(false);
  const [seoChecklist, setSeoChecklist] = useState(null);
  const [loadingChecklist, setLoadingChecklist] = useState(false);
  const [regeneratingSitemap, setRegeneratingSitemap] = useState(false);
  const [sitemapMessage, setSitemapMessage] = useState('');

  // Regenerate sitemap
  const regenerateSitemap = async () => {
    setRegeneratingSitemap(true);
    setSitemapMessage('');
    try {
      const response = await api.post(`/admin/regenerate-sitemap`, { password });
      setSitemapMessage(`✅ ${response.data.message}`);
      setTimeout(() => setSitemapMessage(''), 5000);
    } catch (err) {
      setSitemapMessage('❌ Error regenerating sitemap');
    } finally {
      setRegeneratingSitemap(false);
    }
  };

  // File Manager state
  const [sitemapContent, setSitemapContent] = useState('');
  const [loadingSitemapContent, setLoadingSitemapContent] = useState(false);
  const [showFileManager, setShowFileManager] = useState(false);
  const [copySuccess, setCopySuccess] = useState('');

  // Fetch sitemap content
  const fetchSitemapContent = async () => {
    setLoadingSitemapContent(true);
    try {
      const response = await api.get(`/admin/sitemap-content?password=${password}`);
      setSitemapContent(response.data.content);
    } catch (err) {
      console.error('Error fetching sitemap:', err);
    } finally {
      setLoadingSitemapContent(false);
    }
  };

  // Download sitemap as file
  const downloadSitemap = () => {
    const blob = new Blob([sitemapContent], { type: 'application/xml' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sitemap.xml';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  // Copy sitemap to clipboard
  const copySitemapToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(sitemapContent);
      setCopySuccess('✅ Copied to clipboard!');
      setTimeout(() => setCopySuccess(''), 3000);
    } catch (err) {
      setCopySuccess('❌ Failed to copy');
    }
  };

  // Upload sitemap XML file
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');
  const fileInputRef = React.useRef(null);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xml')) {
      setUploadMessage('❌ Please upload an XML file');
      return;
    }

    setUploadingFile(true);
    setUploadMessage('');

    try {
      const content = await file.text();
      
      // Validate it's valid XML
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(content, 'application/xml');
      const parseError = xmlDoc.querySelector('parsererror');
      
      if (parseError) {
        setUploadMessage('❌ Invalid XML file');
        setUploadingFile(false);
        return;
      }

      // Save to backend
      await api.post(`/admin/sitemap-content`, {
        password,
        content
      });

      setSitemapContent(content);
      setUploadMessage('✅ Sitemap uploaded successfully!');
      setTimeout(() => setUploadMessage(''), 5000);
    } catch (err) {
      setUploadMessage('❌ Error uploading file: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Auth with better error messages and retry
  const [errorCode, setErrorCode] = useState('');
  
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setErrorCode('');
    
    const apiEndpoint = `/analytics/verify`;
    console.log('Admin login attempt to:', apiEndpoint);
    
    // Retry up to 3 times on network errors
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        await api.post(apiEndpoint, { password }, {
          timeout: 15000 // 15 second timeout
        });
        setIsAuthenticated(true);
        sessionStorage.setItem('adminAuth', password);
        setLoading(false);
        return; // Success, exit
      } catch (err) {
        console.error(`Login attempt ${attempt + 1} failed:`, err);
        
        // If it's a 401/429 (auth error), don't retry
        if (err.response?.status === 401 || err.response?.status === 429) {
          const errorMsg = err.response?.data?.detail || 'Invalid password. Access denied.';
          setError(errorMsg);
          setErrorCode(`HTTP_${err.response.status}`);
          setLoading(false);
          return;
        }
        
        // Determine error type
        let code = 'UNKNOWN';
        let message = 'An unexpected error occurred';
        
        if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
          code = 'TIMEOUT';
          message = 'Request timed out. The server took too long to respond.';
        } else if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
          code = 'NETWORK';
          message = 'Network error. Please check your internet connection.';
        } else if (err.request && !err.response) {
          code = 'NO_RESPONSE';
          message = 'No response from server. The server may be temporarily unavailable.';
        }
        
        // Network error - retry
        if (attempt < 2) {
          await new Promise(resolve => setTimeout(resolve, 1500));
          continue;
        }
        
        // Final attempt failed
        setError(message);
        setErrorCode(code);
      }
    }
    setLoading(false);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword('');
    sessionStorage.removeItem('adminAuth');
  };

  // Fetch analytics data
  const fetchAnalytics = useCallback(async (pwd) => {
    try {
      const [summaryRes, liveRes, visitorsRes] = await Promise.all([
        api.get(`/analytics/summary?password=${pwd}`),
        api.get(`/analytics/live?password=${pwd}`),
        api.get(`/analytics/visitors?password=${pwd}&limit=15`)
      ]);
      setSummary(summaryRes.data);
      setLiveStats(liveRes.data);
      setVisitors(visitorsRes.data.visitors);
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
      }
    }
  }, []);

  // Fetch blog posts
  const fetchBlogPosts = useCallback(async (pwd) => {
    try {
      const response = await api.get(`/admin/blog/all?password=${pwd}`);
      setBlogPosts(response.data.posts);
    } catch (err) {
      console.error('Error fetching blog posts:', err);
    }
  }, []);

  // Fetch notification settings
  const fetchNotificationSettings = useCallback(async (pwd) => {
    try {
      const response = await api.get(`/admin/notification-settings?password=${pwd}`);
      setNotificationSettings(response.data);
    } catch (err) {
      console.error('Error fetching notification settings:', err);
    }
  }, []);

  // Save notification settings
  const saveNotificationSettings = async () => {
    setSavingSettings(true);
    setSettingsMessage('');
    try {
      await api.post(`/admin/notification-settings`, {
        password,
        ...notificationSettings
      });
      setSettingsMessage('Settings saved successfully!');
      setTimeout(() => setSettingsMessage(''), 3000);
    } catch (err) {
      setSettingsMessage('Error saving settings');
    } finally {
      setSavingSettings(false);
    }
  };

  // Fetch guides list
  const fetchGuides = useCallback(async (pwd) => {
    try {
      const response = await api.get(`/admin/resources/guides?password=${pwd}`);
      setGuides(response.data.guides || []);
    } catch (err) {
      console.error('Error fetching guides:', err);
    }
  }, []);

  // Fetch specific guide content
  const fetchGuideContent = async (guideId) => {
    setLoadingGuide(true);
    try {
      const response = await api.get(`/admin/resources/guide/${guideId}?password=${password}`);
      setGuideContent(response.data.content);
      setSelectedGuide(guideId);
    } catch (err) {
      console.error('Error fetching guide:', err);
    } finally {
      setLoadingGuide(false);
    }
  };

  // Fetch SEO checklist
  const fetchSeoChecklist = useCallback(async (pwd) => {
    setLoadingChecklist(true);
    try {
      const response = await api.get(`/admin/seo-checklist?password=${pwd}`);
      setSeoChecklist(response.data);
    } catch (err) {
      console.error('Error fetching SEO checklist:', err);
    } finally {
      setLoadingChecklist(false);
    }
  }, []);

  // Toggle checklist item
  const toggleChecklistItem = async (itemId, currentValue) => {
    try {
      await api.post(`/admin/seo-checklist`, {
        password,
        item_id: itemId,
        completed: !currentValue
      });
      fetchSeoChecklist(password);
    } catch (err) {
      console.error('Error updating checklist:', err);
    }
  };

  // Reset checklist
  const resetChecklist = async () => {
    if (!window.confirm('Reset all checklist items? This will clear all your progress.')) return;
    try {
      await api.post(`/admin/seo-checklist/reset`, { password });
      fetchSeoChecklist(password);
    } catch (err) {
      console.error('Error resetting checklist:', err);
    }
  };

  // Check saved session
  useEffect(() => {
    const savedPwd = sessionStorage.getItem('adminAuth');
    if (savedPwd) {
      setPassword(savedPwd);
      setIsAuthenticated(true);
    }
  }, []);

  // Fetch data when authenticated
  useEffect(() => {
    if (isAuthenticated && password) {
      fetchAnalytics(password);
      fetchBlogPosts(password);
      fetchNotificationSettings(password);
      fetchGuides(password);
      fetchSeoChecklist(password);
    }
  }, [isAuthenticated, password, fetchAnalytics, fetchBlogPosts, fetchNotificationSettings, fetchGuides, fetchSeoChecklist]);

  // Auto refresh analytics
  useEffect(() => {
    if (!isAuthenticated || activeTab !== 'analytics') return;
    const interval = setInterval(() => fetchAnalytics(password), 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, activeTab, password, fetchAnalytics]);

  // Create blog post
  const handleCreatePost = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage('');

    try {
      const tagsArray = newPost.tags.split(',').map(t => t.trim()).filter(t => t);
      await api.post(`/admin/blog/create`, {
        password,
        title: newPost.title,
        summary: newPost.summary,
        html_content: newPost.html_content,
        tags: tagsArray,
        thumbnail_url: newPost.thumbnail_url
      });
      
      setMessage('Blog post created successfully!');
      setNewPost({ title: '', summary: '', html_content: '', tags: '', thumbnail_url: '' });
      setShowNewPost(false);
      fetchBlogPosts(password);
    } catch (err) {
      setMessage('Error creating post: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  // Delete blog post (with double confirmation for safety)
  const handleDeletePost = async (slug) => {
    if (!window.confirm('⚠️ Are you sure you want to archive this post?\n\nThe post will be moved to an archive collection and can be restored if needed.')) return;
    
    // Double confirmation to prevent accidental deletion
    const confirmText = window.prompt('Type "DELETE" to confirm archiving this post:');
    if (confirmText !== 'DELETE') {
      alert('Archive cancelled. Post was not deleted.');
      return;
    }
    
    try {
      await api.delete(`/admin/blog/${slug}?password=${password}&confirm=CONFIRM_DELETE`);
      fetchBlogPosts(password);
      alert('Post archived successfully. It can be restored from the database if needed.');
    } catch (err) {
      alert('Error archiving post: ' + (err.response?.data?.detail || err.message));
    }
  };

  // Toggle publish status
  const handleTogglePost = async (slug) => {
    try {
      await api.put(`/admin/blog/${slug}/toggle?password=${password}`);
      fetchBlogPosts(password);
    } catch (err) {
      alert('Error toggling post');
    }
  };

  // View blog post
  const handleViewPost = async (slug) => {
    try {
      const response = await api.get(`/admin/blog/${slug}?password=${password}`);
      setViewingPost(response.data);
    } catch (err) {
      alert('Error loading post');
    }
  };

  // Start editing a post
  const handleStartEdit = async (slug) => {
    try {
      const response = await api.get(`/admin/blog/${slug}?password=${password}`);
      const post = response.data;
      setEditingPost({
        slug: post.slug,
        title: post.title,
        summary: post.summary || '',
        html_content: post.html_content,
        tags: post.tags ? post.tags.join(', ') : '',
        thumbnail_url: post.thumbnail_url || ''
      });
      setShowNewPost(false);
      setViewingPost(null);
    } catch (err) {
      alert('Error loading post for editing');
    }
  };

  // Update blog post
  const handleUpdatePost = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage('');

    try {
      const tagsArray = editingPost.tags.split(',').map(t => t.trim()).filter(t => t);
      await api.put(`/admin/blog/${editingPost.slug}`, {
        password,
        title: editingPost.title,
        summary: editingPost.summary,
        html_content: editingPost.html_content,
        tags: tagsArray,
        thumbnail_url: editingPost.thumbnail_url
      });
      
      setMessage('Blog post updated successfully!');
      setEditingPost(null);
      fetchBlogPosts(password);
    } catch (err) {
      setMessage('Error updating post: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'radial-gradient(circle at center, #262626 0%, #121212 100%)',
        padding: '20px',
        fontFamily: 'var(--font-body)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Crown pattern overlay */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.05,
          backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='96' height='96' viewBox='0 0 96 96'><g fill='none' stroke='%23D4AF37' stroke-width='1'><path d='M48 16l6 11 11-4-4 13H35l-4-13 11 4z'/><circle cx='48' cy='62' r='7'/><rect x='40' y='64' width='16' height='14' rx='2'/></g></svg>\")",
          backgroundSize: '96px 96px'
        }} />
        {/* Union Jack hairline */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: '1px',
          background: 'linear-gradient(90deg, transparent 0%, var(--royal-gold) 50%, transparent 100%)'
        }} />

        <div style={{
          background: '#1E1E1E',
          padding: '48px 40px',
          borderRadius: '14px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(212, 175, 55, 0.25)',
          maxWidth: '420px',
          width: '100%',
          position: 'relative',
          zIndex: 2
        }} data-testid="admin-login-card">
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <img
              src="/brand/royal-logo-wordmark.png"
              alt="Royal Security"
              style={{ height: '70px', width: 'auto', margin: '0 auto 20px', display: 'block' }}
            />
            <div style={{
              width: '56px',
              height: '56px',
              background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.2) 0%, rgba(27, 42, 78, 0.4) 100%)',
              border: '1px solid var(--royal-gold-border)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px'
            }}>
              <Lock size={28} color="var(--royal-gold-bright)" />
            </div>
            <h1 style={{
              fontFamily: 'var(--font-display)',
              fontSize: '1.5rem',
              color: '#FFFFFF',
              letterSpacing: '0.08em',
              marginBottom: '6px'
            }}>
              Command Centre
            </h1>
            <p style={{ color: 'var(--text-on-dark-dimmed)', fontSize: '0.85rem', letterSpacing: '0.04em' }}>
              Royal Security Admin Dashboard
            </p>
            <p style={{ color: '#555', fontSize: '0.7rem', marginTop: '6px', letterSpacing: '0.1em' }}>
              VERSION 2.0.0
            </p>
          </div>

          <form onSubmit={handleLogin}>
            <label style={{
              display: 'block',
              fontSize: '0.75rem',
              fontWeight: '700',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: 'var(--royal-gold-bright)',
              marginBottom: '8px'
            }}>
              Admin Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              data-testid="admin-password-input"
              className="royal-input royal-input-dark"
              style={{ marginBottom: '16px', padding: '14px 16px', fontSize: '0.95rem' }}
              autoFocus
            />

            {error && (
              <div style={{
                background: 'rgba(191, 10, 48, 0.15)',
                color: '#FF6B81',
                padding: '12px 14px',
                borderRadius: '6px',
                marginBottom: '16px',
                fontSize: '0.875rem',
                border: '1px solid rgba(191, 10, 48, 0.3)'
              }} data-testid="admin-login-error">
                {error}
                {errorCode && (
                  <div style={{
                    marginTop: '8px',
                    fontSize: '0.72rem',
                    fontFamily: 'monospace',
                    color: '#999',
                    background: 'rgba(0, 0, 0, 0.3)',
                    padding: '6px 10px',
                    borderRadius: '4px'
                  }}>
                    Error Code: {errorCode}
                  </div>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-button"
              className="royal-btn-primary"
              style={{
                width: '100%',
                justifyContent: 'center',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Verifying...' : 'Access Dashboard'}
            </button>
          </form>

          <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
            <Link
              to="/"
              style={{
                color: 'var(--text-on-dark-muted)',
                fontSize: '0.875rem',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'color 0.2s ease'
              }}
              onMouseEnter={(e) => (e.target.style.color = 'var(--royal-gold-bright)')}
              onMouseLeave={(e) => (e.target.style.color = 'var(--text-on-dark-muted)')}
            >
              ← Back to Website
            </Link>
            <button
              onClick={() => window.location.reload()}
              style={{
                background: 'none',
                border: 'none',
                color: '#666',
                fontSize: '0.75rem',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              Having issues? Click to refresh
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f5f6fa' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(90deg, var(--royal-dark-deepest) 0%, var(--royal-dark-charcoal) 100%)',
        padding: '14px 0',
        color: 'white',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        borderBottom: '1px solid rgba(212, 175, 55, 0.25)',
        boxShadow: '0 2px 20px rgba(0, 0, 0, 0.3)'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 16px' }}>
          {/* Mobile-friendly header */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <a 
              href="/" 
              style={{ 
                fontSize: '1rem', 
                fontWeight: '700', 
                color: 'white', 
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                whiteSpace: 'nowrap'
              }}
            >
              👑 <span style={{ display: 'none' }} className="hide-mobile">Royal Security</span> Admin
            </a>
            
            {/* Tabs - scrollable on mobile */}
            <div style={{ 
              display: 'flex', 
              gap: '4px',
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
              flex: '1',
              justifyContent: 'center',
              minWidth: 0
            }}>
              <button
                onClick={() => setActiveTab('analytics')}
                style={{
                  padding: '8px 12px',
                  background: activeTab === 'analytics' ? 'rgba(200,168,96,0.3)' : 'transparent',
                  border: 'none',
                  borderRadius: '6px',
                  color: 'white',
                  cursor: 'pointer',
                  fontWeight: activeTab === 'analytics' ? '600' : '400',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.85rem',
                  whiteSpace: 'nowrap'
                }}
              >
                <BarChart3 size={16} />
                <span>Analytics</span>
              </button>
              <button
                onClick={() => setActiveTab('blog')}
                style={{
                  padding: '8px 12px',
                  background: activeTab === 'blog' ? 'rgba(200,168,96,0.3)' : 'transparent',
                  border: 'none',
                  borderRadius: '6px',
                  color: 'white',
                  cursor: 'pointer',
                  fontWeight: activeTab === 'blog' ? '600' : '400',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.85rem',
                  whiteSpace: 'nowrap'
                }}
              >
                <FileText size={16} />
                <span>Blog</span>
              </button>
              <button
                onClick={() => setActiveTab('resources')}
                style={{
                  padding: '8px 12px',
                  background: activeTab === 'resources' ? 'rgba(200,168,96,0.3)' : 'transparent',
                  border: 'none',
                  borderRadius: '6px',
                  color: 'white',
                  cursor: 'pointer',
                  fontWeight: activeTab === 'resources' ? '600' : '400',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.85rem',
                  whiteSpace: 'nowrap'
                }}
              >
                <BookOpen size={16} />
                <span>Resources</span>
              </button>
              <button
                onClick={() => setActiveTab('settings')}
                style={{
                  padding: '8px 12px',
                  background: activeTab === 'settings' ? 'rgba(200,168,96,0.3)' : 'transparent',
                  border: 'none',
                  borderRadius: '6px',
                  color: 'white',
                  cursor: 'pointer',
                  fontWeight: activeTab === 'settings' ? '600' : '400',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.85rem',
                  whiteSpace: 'nowrap'
                }}
              >
                <Activity size={16} />
                <span>Settings</span>
              </button>
            </div>
            
            <button
              onClick={handleLogout}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 12px',
                background: 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: '6px',
                color: 'white',
                cursor: 'pointer'
              }}
            >
              <LogOut size={18} />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '16px' }}>
        
        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <>
            {/* Live Stats */}
            {liveStats && (
              <div style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                borderRadius: '12px',
                padding: '12px 16px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '16px',
                color: 'white'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Activity size={24} />
                  <div>
                    <span style={{ fontWeight: '700', fontSize: '1.5rem' }}>{liveStats.active_now}</span>
                    <span style={{ marginLeft: '8px', opacity: 0.9 }}>active now</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '24px' }}>
                  <div><span style={{ opacity: 0.8, fontSize: '0.8rem' }}>Hour</span><div style={{ fontWeight: '600' }}>{liveStats.last_hour}</div></div>
                  <div><span style={{ opacity: 0.8, fontSize: '0.8rem' }}>Today</span><div style={{ fontWeight: '600' }}>{liveStats.today}</div></div>
                  <div><span style={{ opacity: 0.8, fontSize: '0.8rem' }}>Total</span><div style={{ fontWeight: '600' }}>{liveStats.total}</div></div>
                </div>
                <button onClick={() => fetchAnalytics(password)} style={{
                  display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px',
                  background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '6px',
                  color: 'white', cursor: 'pointer', fontWeight: '500'
                }}>
                  <RefreshCw size={16} /> Refresh
                </button>
              </div>
            )}

            {/* Summary Stats */}
            {summary && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                <StatCard icon={Users} label="Total" value={summary.summary.total} color="#1A1A1A" />
                <StatCard icon={Calendar} label="Today" value={summary.summary.today} color="#10b981" />
                <StatCard icon={TrendingUp} label="Week" value={summary.summary.this_week} color="#6366f1" />
                <StatCard icon={BarChart3} label="Month" value={summary.summary.this_month} color="#f59e0b" />
              </div>
            )}

            {/* Charts */}
            {summary && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                <div style={{ background: 'white', padding: '16px', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <h3 style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '12px', color: '#1A1A1A' }}>Top Pages</h3>
                  <SimpleBarChart data={summary.top_pages} labelKey="name" valueKey="count" color="#6366f1" />
                </div>
                <div style={{ background: 'white', padding: '16px', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <h3 style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '12px', color: '#1A1A1A' }}>Locations</h3>
                  <SimpleBarChart data={summary.top_cities} labelKey="name" valueKey="count" color="#10b981" />
                </div>
                <div style={{ background: 'white', padding: '16px', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <h3 style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '12px', color: '#1A1A1A' }}>Devices</h3>
                  <SimpleBarChart data={summary.devices} labelKey="name" valueKey="count" color="#f59e0b" />
                </div>
                <div style={{ background: 'white', padding: '16px', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <h3 style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '12px', color: '#1A1A1A' }}>Sources</h3>
                  <SimpleBarChart data={summary.referrers.map(r => ({ ...r, name: formatReferrer(r.name) }))} labelKey="name" valueKey="count" color="#14b8a6" />
                </div>
              </div>
            )}

            {/* Recent Visitors */}
            <div style={{ background: 'white', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid #eee' }}>
                <h3 style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1A1A1A', margin: 0 }}>Recent Visitors</h3>
              </div>
              <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem', minWidth: '500px' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa' }}>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', color: '#666', whiteSpace: 'nowrap' }}>#</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', color: '#666', whiteSpace: 'nowrap' }}>Time</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', color: '#666', whiteSpace: 'nowrap' }}>Location</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', color: '#666', whiteSpace: 'nowrap' }}>Page</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', color: '#666', whiteSpace: 'nowrap' }}>Device</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visitors.map((v, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '8px 10px', fontWeight: '600', color: '#C8A860', whiteSpace: 'nowrap' }}>#{v.visitor_number}</td>
                        <td style={{ padding: '8px 10px', color: '#666', whiteSpace: 'nowrap' }}>{v.timestamp ? new Date(v.timestamp).toLocaleString() : '-'}</td>
                        <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{v.city}, {v.country}</td>
                        <td style={{ padding: '8px 10px', color: '#6366f1', whiteSpace: 'nowrap' }}>{v.page}</td>
                        <td style={{ padding: '8px 10px', color: '#666', whiteSpace: 'nowrap' }}>{v.device_type}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Blog Tab */}
        {activeTab === 'blog' && (
          <>
            {/* View Post Modal */}
            {viewingPost && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.7)',
                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px'
              }}>
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  maxWidth: '900px',
                  width: '100%',
                  maxHeight: '90vh',
                  overflow: 'auto',
                  position: 'relative'
                }}>
                  <div style={{
                    position: 'sticky',
                    top: 0,
                    background: 'white',
                    padding: '20px 24px',
                    borderBottom: '1px solid #eee',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <h2 style={{ color: '#1A1A1A', margin: 0, fontSize: '1.25rem' }}>{viewingPost.title}</h2>
                      <p style={{ color: '#888', fontSize: '0.85rem', margin: '4px 0 0' }}>
                        /{viewingPost.slug} • {viewingPost.published ? 'Published' : 'Draft'}
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => { handleStartEdit(viewingPost.slug); }}
                        style={{
                          padding: '8px 16px',
                          background: '#1A1A1A',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <Edit3 size={16} /> Edit
                      </button>
                      <button
                        onClick={() => setViewingPost(null)}
                        style={{
                          padding: '8px 16px',
                          background: '#666',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                  <div style={{ padding: '24px' }}>
                    {viewingPost.thumbnail_url && (
                      <div style={{ marginBottom: '20px' }}>
                        <strong style={{ color: '#1A1A1A', fontSize: '0.9rem' }}>Thumbnail:</strong>
                        <div style={{ marginTop: '8px' }}>
                          <img 
                            src={viewingPost.thumbnail_url} 
                            alt="Thumbnail" 
                            style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px' }}
                          />
                        </div>
                      </div>
                    )}
                    {viewingPost.summary && (
                      <div style={{
                        background: '#f8f9fa',
                        padding: '16px',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        borderLeft: '4px solid #C8A860'
                      }}>
                        <strong style={{ color: '#1A1A1A' }}>Summary:</strong>
                        <p style={{ margin: '8px 0 0', color: '#555' }}>{viewingPost.summary}</p>
                      </div>
                    )}
                    {viewingPost.tags && viewingPost.tags.length > 0 && (
                      <div style={{ marginBottom: '20px' }}>
                        <strong style={{ color: '#1A1A1A', fontSize: '0.9rem' }}>Tags:</strong>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                          {viewingPost.tags.map((tag, i) => (
                            <span key={i} style={{
                              padding: '4px 12px',
                              background: '#e8e8e8',
                              borderRadius: '20px',
                              fontSize: '0.8rem',
                              color: '#555'
                            }}>{tag}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    <div style={{
                      background: '#f8f9fa',
                      padding: '16px',
                      borderRadius: '8px',
                      marginBottom: '20px'
                    }}>
                      <strong style={{ color: '#1A1A1A', fontSize: '0.9rem' }}>HTML Content Preview:</strong>
                      <div 
                        style={{
                          marginTop: '12px',
                          padding: '16px',
                          background: 'white',
                          borderRadius: '6px',
                          border: '1px solid #e8e8e8',
                          lineHeight: '1.7'
                        }}
                        dangerouslySetInnerHTML={{ __html: viewingPost.html_content }}
                      />
                    </div>
                    <div style={{
                      background: '#1A1A1A',
                      padding: '12px 16px',
                      borderRadius: '8px'
                    }}>
                      <strong style={{ color: '#C8A860', fontSize: '0.85rem' }}>Raw HTML:</strong>
                      <pre style={{
                        marginTop: '8px',
                        padding: '12px',
                        background: '#0d1520',
                        borderRadius: '6px',
                        color: '#10b981',
                        fontSize: '0.8rem',
                        overflow: 'auto',
                        maxHeight: '300px',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all'
                      }}>{viewingPost.html_content}</pre>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1A1A1A' }}>Blog Posts</h2>
              <button
                onClick={() => { setShowNewPost(!showNewPost); setEditingPost(null); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 24px',
                  background: showNewPost ? '#666' : '#1A1A1A',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {showNewPost ? 'Cancel' : <><Plus size={18} /> New Post</>}
              </button>
            </div>

            {/* Message */}
            {message && (
              <div style={{
                padding: '16px',
                marginBottom: '20px',
                borderRadius: '8px',
                background: message.includes('Error') ? '#fee' : '#efe',
                color: message.includes('Error') ? '#c00' : '#060'
              }}>
                {message}
              </div>
            )}

            {/* Edit Post Form */}
            {editingPost && (
              <div style={{
                background: 'white',
                padding: '24px',
                borderRadius: '12px',
                marginBottom: '24px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                border: '2px solid #f59e0b'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3 style={{ color: '#1A1A1A', margin: 0 }}>
                    <Edit3 size={20} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                    Editing: {editingPost.title}
                  </h3>
                  <button
                    onClick={() => setEditingPost(null)}
                    style={{
                      padding: '8px 16px',
                      background: '#666',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel Edit
                  </button>
                </div>
                <form onSubmit={handleUpdatePost}>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#333' }}>Title *</label>
                    <input
                      type="text"
                      value={editingPost.title}
                      onChange={(e) => setEditingPost({ ...editingPost, title: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e8e8e8',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#333' }}>Thumbnail URL</label>
                    <input
                      type="url"
                      value={editingPost.thumbnail_url}
                      onChange={(e) => setEditingPost({ ...editingPost, thumbnail_url: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e8e8e8',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        boxSizing: 'border-box'
                      }}
                      placeholder="https://example.com/image.jpg"
                    />
                    {editingPost.thumbnail_url && (
                      <div style={{ marginTop: '8px' }}>
                        <img 
                          src={editingPost.thumbnail_url} 
                          alt="Thumbnail preview" 
                          style={{ maxHeight: '100px', borderRadius: '4px' }}
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      </div>
                    )}
                  </div>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#333' }}>Summary</label>
                    <textarea
                      value={editingPost.summary}
                      onChange={(e) => setEditingPost({ ...editingPost, summary: e.target.value })}
                      rows={2}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e8e8e8',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        boxSizing: 'border-box',
                        resize: 'vertical'
                      }}
                    />
                  </div>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#333' }}>Tags (comma separated)</label>
                    <input
                      type="text"
                      value={editingPost.tags}
                      onChange={(e) => setEditingPost({ ...editingPost, tags: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e8e8e8',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#333' }}>HTML Content *</label>
                    <textarea
                      value={editingPost.html_content}
                      onChange={(e) => setEditingPost({ ...editingPost, html_content: e.target.value })}
                      required
                      rows={15}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e8e8e8',
                        borderRadius: '8px',
                        fontSize: '0.9rem',
                        fontFamily: 'monospace',
                        boxSizing: 'border-box',
                        resize: 'vertical'
                      }}
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={submitting}
                    style={{
                      padding: '14px 32px',
                      background: '#f59e0b',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      cursor: submitting ? 'not-allowed' : 'pointer',
                      opacity: submitting ? 0.7 : 1
                    }}
                  >
                    {submitting ? 'Saving...' : 'Save Changes'}
                  </button>
                </form>
              </div>
            )}

            {/* New Post Form */}
            {showNewPost && !editingPost && (
              <div style={{
                background: 'white',
                padding: '24px',
                borderRadius: '12px',
                marginBottom: '24px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
              }}>
                <h3 style={{ marginBottom: '20px', color: '#1A1A1A' }}>Create New Blog Post</h3>
                <form onSubmit={handleCreatePost}>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#333' }}>Title *</label>
                    <input
                      type="text"
                      value={newPost.title}
                      onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e8e8e8',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        boxSizing: 'border-box'
                      }}
                      placeholder="Enter blog post title"
                    />
                  </div>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#333' }}>Thumbnail URL</label>
                    <input
                      type="url"
                      value={newPost.thumbnail_url}
                      onChange={(e) => setNewPost({ ...newPost, thumbnail_url: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e8e8e8',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        boxSizing: 'border-box'
                      }}
                      placeholder="https://example.com/image.jpg"
                    />
                    <p style={{ fontSize: '0.8rem', color: '#888', marginTop: '4px' }}>
                      Optional: Featured image displayed at top of blog post
                    </p>
                  </div>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#333' }}>Summary (for preview)</label>
                    <textarea
                      value={newPost.summary}
                      onChange={(e) => setNewPost({ ...newPost, summary: e.target.value })}
                      rows={2}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e8e8e8',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        boxSizing: 'border-box',
                        resize: 'vertical'
                      }}
                      placeholder="Brief summary shown on blog listing page"
                    />
                  </div>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#333' }}>Tags (comma separated)</label>
                    <input
                      type="text"
                      value={newPost.tags}
                      onChange={(e) => setNewPost({ ...newPost, tags: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e8e8e8',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        boxSizing: 'border-box'
                      }}
                      placeholder="e.g. Door Repairs, Security Tips, Windows"
                    />
                  </div>
                  
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#333' }}>HTML Content *</label>
                    <textarea
                      value={newPost.html_content}
                      onChange={(e) => setNewPost({ ...newPost, html_content: e.target.value })}
                      required
                      rows={15}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e8e8e8',
                        borderRadius: '8px',
                        fontSize: '0.9rem',
                        fontFamily: 'monospace',
                        boxSizing: 'border-box',
                        resize: 'vertical'
                      }}
                      placeholder="Paste your HTML content here..."
                    />
                    <p style={{ fontSize: '0.8rem', color: '#888', marginTop: '8px' }}>
                      Paste the full HTML content for your blog post. Supported tags: h1-h4, p, ul, ol, li, a, strong, em, blockquote, img
                    </p>
                  </div>
                  
                  <button
                    type="submit"
                    disabled={submitting}
                    style={{
                      padding: '14px 32px',
                      background: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      cursor: submitting ? 'not-allowed' : 'pointer',
                      opacity: submitting ? 0.7 : 1
                    }}
                  >
                    {submitting ? 'Publishing...' : 'Publish Blog Post'}
                  </button>
                </form>
              </div>
            )}

            {/* Posts List */}
            <div style={{ background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              {blogPosts.length === 0 ? (
                <div style={{ padding: '60px', textAlign: 'center', color: '#888' }}>
                  <FileText size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                  <p>No blog posts yet. Create your first post above!</p>
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa' }}>
                      <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', color: '#666' }}>Title</th>
                      <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', color: '#666' }}>Date</th>
                      <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '600', color: '#666' }}>Status</th>
                      <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '600', color: '#666' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blogPosts.map((post) => (
                      <tr key={post.slug} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '14px 16px' }}>
                          <strong style={{ color: '#1A1A1A' }}>{post.title}</strong>
                          <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '4px' }}>/{post.slug}</div>
                        </td>
                        <td style={{ padding: '14px 16px', color: '#666', fontSize: '0.9rem' }}>
                          {post.created_at ? new Date(post.created_at).toLocaleDateString() : '-'}
                        </td>
                        <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '20px',
                            fontSize: '0.8rem',
                            fontWeight: '500',
                            background: post.published ? '#dcfce7' : '#fef3c7',
                            color: post.published ? '#166534' : '#92400e'
                          }}>
                            {post.published ? 'Published' : 'Draft'}
                          </span>
                        </td>
                        <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                            <button
                              onClick={() => handleViewPost(post.slug)}
                              title="View Post"
                              style={{
                                padding: '8px',
                                background: '#e0f2fe',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer'
                              }}
                            >
                              <Eye size={18} color="#0284c7" />
                            </button>
                            <button
                              onClick={() => handleStartEdit(post.slug)}
                              title="Edit Post"
                              style={{
                                padding: '8px',
                                background: '#fef3c7',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer'
                              }}
                            >
                              <Edit3 size={18} color="#d97706" />
                            </button>
                            <button
                              onClick={() => handleTogglePost(post.slug)}
                              title={post.published ? 'Unpublish' : 'Publish'}
                              style={{
                                padding: '8px',
                                background: '#f0f0f0',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer'
                              }}
                            >
                              {post.published ? <ToggleRight size={18} color="#10b981" /> : <ToggleLeft size={18} color="#888" />}
                            </button>
                            <a
                              href={`/blog/${post.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                padding: '8px',
                                background: '#f0f0f0',
                                border: 'none',
                                borderRadius: '6px',
                                display: 'inline-flex'
                              }}
                            >
                              <ExternalLink size={18} color="#666" />
                            </a>
                            <button
                              onClick={() => handleDeletePost(post.slug)}
                              title="Delete"
                              style={{
                                padding: '8px',
                                background: '#fee',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer'
                              }}
                            >
                              <Trash2 size={18} color="#c00" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1A1A1A', marginBottom: '24px' }}>
              Notification Settings
            </h2>

            {/* Settings Message */}
            {settingsMessage && (
              <div style={{
                padding: '16px',
                marginBottom: '20px',
                borderRadius: '8px',
                background: settingsMessage.includes('Error') ? '#fee' : '#dcfce7',
                color: settingsMessage.includes('Error') ? '#c00' : '#166534'
              }}>
                {settingsMessage}
              </div>
            )}

            <div style={{ 
              background: 'white', 
              borderRadius: '12px', 
              padding: '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              maxWidth: '600px'
            }}>
              {/* Instant Notifications Toggle */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 0',
                borderBottom: '1px solid #eee'
              }}>
                <div>
                  <div style={{ fontWeight: '600', color: '#1A1A1A', marginBottom: '4px' }}>
                    Instant Visitor Notifications
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#666' }}>
                    Receive an email each time a new visitor arrives on your site
                  </div>
                </div>
                <button
                  onClick={() => setNotificationSettings({
                    ...notificationSettings,
                    instantNotifications: !notificationSettings.instantNotifications
                  })}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px'
                  }}
                >
                  {notificationSettings.instantNotifications ? (
                    <ToggleRight size={36} color="#10b981" />
                  ) : (
                    <ToggleLeft size={36} color="#ccc" />
                  )}
                </button>
              </div>

              {/* Daily Summary Toggle */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 0',
                borderBottom: '1px solid #eee'
              }}>
                <div>
                  <div style={{ fontWeight: '600', color: '#1A1A1A', marginBottom: '4px' }}>
                    Daily Summary Email
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#666' }}>
                    Receive a daily summary of all visitors at the end of each day
                  </div>
                </div>
                <button
                  onClick={() => setNotificationSettings({
                    ...notificationSettings,
                    dailySummary: !notificationSettings.dailySummary
                  })}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px'
                  }}
                >
                  {notificationSettings.dailySummary ? (
                    <ToggleRight size={36} color="#10b981" />
                  ) : (
                    <ToggleLeft size={36} color="#ccc" />
                  )}
                </button>
              </div>

              {/* Summary Time (only shown if daily summary is enabled) */}
              {notificationSettings.dailySummary && (
                <div style={{
                  padding: '16px 0',
                  borderBottom: '1px solid #eee'
                }}>
                  <div style={{ fontWeight: '600', color: '#1A1A1A', marginBottom: '8px' }}>
                    Summary Email Time
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '12px' }}>
                    Choose when to receive your daily visitor summary
                  </div>
                  <select
                    value={notificationSettings.summaryTime}
                    onChange={(e) => setNotificationSettings({
                      ...notificationSettings,
                      summaryTime: e.target.value
                    })}
                    style={{
                      padding: '10px 16px',
                      border: '2px solid #e8e8e8',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      background: 'white',
                      cursor: 'pointer',
                      minWidth: '150px'
                    }}
                  >
                    <option value="09:00">9:00 AM</option>
                    <option value="12:00">12:00 PM</option>
                    <option value="17:00">5:00 PM</option>
                    <option value="18:00">6:00 PM</option>
                    <option value="20:00">8:00 PM</option>
                    <option value="21:00">9:00 PM</option>
                  </select>
                </div>
              )}

              {/* Visitor Filter */}
              <div style={{
                padding: '16px 0'
              }}>
                <div style={{ fontWeight: '600', color: '#1A1A1A', marginBottom: '8px' }}>
                  Visitors to Display
                </div>
                <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '12px' }}>
                  Filter which visitors to show in the analytics dashboard
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={() => setVisitorFilter('all')}
                    style={{
                      padding: '10px 20px',
                      border: '2px solid',
                      borderColor: visitorFilter === 'all' ? '#1A1A1A' : '#e8e8e8',
                      background: visitorFilter === 'all' ? '#1A1A1A' : 'white',
                      color: visitorFilter === 'all' ? 'white' : '#333',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    All Visitors
                  </button>
                  <button
                    onClick={() => setVisitorFilter('new')}
                    style={{
                      padding: '10px 20px',
                      border: '2px solid',
                      borderColor: visitorFilter === 'new' ? '#1A1A1A' : '#e8e8e8',
                      background: visitorFilter === 'new' ? '#1A1A1A' : 'white',
                      color: visitorFilter === 'new' ? 'white' : '#333',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    New Visitors Only
                  </button>
                </div>
              </div>

              {/* Save Button */}
              <div style={{ marginTop: '24px' }}>
                <button
                  onClick={saveNotificationSettings}
                  disabled={savingSettings}
                  style={{
                    padding: '14px 32px',
                    background: '#1A1A1A',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: savingSettings ? 'not-allowed' : 'pointer',
                    opacity: savingSettings ? 0.7 : 1
                  }}
                >
                  {savingSettings ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>

            {/* Current Settings Info */}
            <div style={{
              background: '#f8f9fa',
              borderRadius: '12px',
              padding: '20px',
              marginTop: '24px',
              maxWidth: '600px'
            }}>
              <div style={{ fontWeight: '600', color: '#1A1A1A', marginBottom: '12px' }}>
                Current Configuration
              </div>
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#666', lineHeight: '1.8' }}>
                <li>
                  Instant notifications: <strong style={{ color: notificationSettings.instantNotifications ? '#10b981' : '#ef4444' }}>
                    {notificationSettings.instantNotifications ? 'ON' : 'OFF'}
                  </strong>
                </li>
                <li>
                  Daily summary: <strong style={{ color: notificationSettings.dailySummary ? '#10b981' : '#ef4444' }}>
                    {notificationSettings.dailySummary ? `ON (at ${notificationSettings.summaryTime})` : 'OFF'}
                  </strong>
                </li>
                <li>
                  Showing: <strong>{visitorFilter === 'all' ? 'All visitors' : 'New visitors only'}</strong>
                </li>
              </ul>
            </div>
          </>
        )}

        {/* Resources Tab */}
        {activeTab === 'resources' && (
          <>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1A1A1A', marginBottom: '24px' }}>
              Resources & Tools
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
              {/* Guides Section */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                border: '1px solid #e8e8e8'
              }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1A1A1A', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <BookOpen size={20} />
                  Guides & Documentation
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {guides.map((guide) => (
                    <button
                      key={guide.id}
                      onClick={() => fetchGuideContent(guide.id)}
                      style={{
                        padding: '16px',
                        background: selectedGuide === guide.id ? '#f0f7ff' : '#f8f9fa',
                        border: selectedGuide === guide.id ? '2px solid #1A1A1A' : '1px solid #e8e8e8',
                        borderRadius: '8px',
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      <div style={{ fontWeight: '600', color: '#1A1A1A', marginBottom: '4px' }}>
                        {guide.title}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#666' }}>
                        {guide.description}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* SEO Tools Section */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                border: '1px solid #e8e8e8'
              }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1A1A1A', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Search size={20} />
                  SEO Tools
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <a
                    href="https://search.google.com/search-console"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: '16px',
                      background: '#f8f9fa',
                      border: '1px solid #e8e8e8',
                      borderRadius: '8px',
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: '600', color: '#1A1A1A', marginBottom: '4px' }}>
                        Google Search Console
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#666' }}>
                        Monitor search performance & indexing
                      </div>
                    </div>
                    <ExternalLink size={18} color="#666" />
                  </a>
                  <a
                    href="https://analytics.google.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: '16px',
                      background: '#f8f9fa',
                      border: '1px solid #e8e8e8',
                      borderRadius: '8px',
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: '600', color: '#1A1A1A', marginBottom: '4px' }}>
                        Google Analytics
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#666' }}>
                        Track visitor behavior & conversions
                      </div>
                    </div>
                    <ExternalLink size={18} color="#666" />
                  </a>
                  <a
                    href="https://www.bing.com/webmasters"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: '16px',
                      background: '#f8f9fa',
                      border: '1px solid #e8e8e8',
                      borderRadius: '8px',
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: '600', color: '#1A1A1A', marginBottom: '4px' }}>
                        Bing Webmaster Tools
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#666' }}>
                        Don't forget Bing search traffic!
                      </div>
                    </div>
                    <ExternalLink size={18} color="#666" />
                  </a>
                  <div style={{
                    padding: '16px',
                    background: '#e8f5e9',
                    border: '1px solid #c8e6c9',
                    borderRadius: '8px'
                  }}>
                    <div style={{ fontWeight: '600', color: '#2e7d32', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <LinkIcon size={16} />
                      Your Sitemap URL
                    </div>
                    <code style={{ fontSize: '0.8rem', color: '#1b5e20', wordBreak: 'break-all', display: 'block', marginBottom: '12px' }}>
                      https://royalsecurity.info/api/sitemap.xml
                    </code>
                    <button
                      onClick={regenerateSitemap}
                      disabled={regeneratingSitemap}
                      style={{
                        padding: '8px 16px',
                        background: regeneratingSitemap ? '#ccc' : '#2e7d32',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: regeneratingSitemap ? 'not-allowed' : 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: '500'
                      }}
                    >
                      {regeneratingSitemap ? 'Regenerating...' : 'Regenerate Sitemap'}
                    </button>
                    {sitemapMessage && (
                      <div style={{ marginTop: '8px', fontSize: '0.85rem', color: sitemapMessage.includes('✅') ? '#2e7d32' : '#d32f2f' }}>
                        {sitemapMessage}
                      </div>
                    )}
                    <p style={{ fontSize: '0.75rem', color: '#666', marginTop: '8px', marginBottom: '12px' }}>
                      Click after publishing new blog posts to update the sitemap
                    </p>
                    <button
                      onClick={() => { setShowFileManager(true); fetchSitemapContent(); }}
                      style={{
                        padding: '8px 16px',
                        background: '#1A1A1A',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <FileCode size={16} />
                      Open File Manager
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* File Manager Modal */}
            {showFileManager && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '20px'
              }}>
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  maxWidth: '900px',
                  width: '100%',
                  maxHeight: '90vh',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <div style={{
                    padding: '20px 24px',
                    borderBottom: '1px solid #e8e8e8',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <h3 style={{ margin: 0, color: '#1A1A1A', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FileCode size={20} />
                      Sitemap File Manager
                    </h3>
                    <button
                      onClick={() => setShowFileManager(false)}
                      style={{
                        background: '#f8f9fa',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px',
                        cursor: 'pointer'
                      }}
                    >
                      <X size={20} />
                    </button>
                  </div>
                  
                  <div style={{ padding: '20px 24px', borderBottom: '1px solid #e8e8e8' }}>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                      {/* Upload Button */}
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept=".xml"
                        onChange={handleFileUpload}
                        style={{ display: 'none' }}
                      />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingFile}
                        style={{
                          padding: '10px 20px',
                          background: uploadingFile ? '#ccc' : '#C8A860',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: uploadingFile ? 'not-allowed' : 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        <FileCode size={18} />
                        {uploadingFile ? 'Uploading...' : 'Upload sitemap.xml'}
                      </button>
                      <button
                        onClick={downloadSitemap}
                        disabled={!sitemapContent}
                        style={{
                          padding: '10px 20px',
                          background: sitemapContent ? '#2e7d32' : '#ccc',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: sitemapContent ? 'pointer' : 'not-allowed',
                          fontSize: '0.9rem',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        <Download size={18} />
                        Download
                      </button>
                      <button
                        onClick={copySitemapToClipboard}
                        disabled={!sitemapContent}
                        style={{
                          padding: '10px 20px',
                          background: sitemapContent ? '#1A1A1A' : '#ccc',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: sitemapContent ? 'pointer' : 'not-allowed',
                          fontSize: '0.9rem',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        <Copy size={18} />
                        Copy
                      </button>
                    </div>
                    
                    {(copySuccess || uploadMessage) && (
                      <div style={{ 
                        marginTop: '12px',
                        padding: '10px 16px', 
                        fontSize: '0.9rem',
                        color: (copySuccess + uploadMessage).includes('✅') ? '#2e7d32' : '#d32f2f',
                        background: (copySuccess + uploadMessage).includes('✅') ? '#e8f5e9' : '#ffebee',
                        borderRadius: '6px'
                      }}>
                        {copySuccess || uploadMessage}
                      </div>
                    )}
                    
                    <div style={{ 
                      marginTop: '16px', 
                      padding: '12px 16px', 
                      background: '#e3f2fd', 
                      border: '1px solid #2196f3',
                      borderRadius: '8px',
                      fontSize: '0.85rem',
                      color: '#1565c0'
                    }}>
                      <strong>Your working sitemap URL (submit this to Google):</strong>
                      <code style={{ display: 'block', marginTop: '8px', background: '#fff', padding: '8px', borderRadius: '4px', wordBreak: 'break-all' }}>
                        https://royalsecurity.info/api/sitemap.xml
                      </code>
                    </div>
                  </div>
                  
                  <div style={{
                    padding: '20px 24px',
                    overflow: 'auto',
                    flex: 1,
                    background: '#1e1e1e'
                  }}>
                    {loadingSitemapContent ? (
                      <p style={{ color: '#888' }}>Loading sitemap content...</p>
                    ) : (
                      <pre style={{
                        margin: 0,
                        fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                        fontSize: '0.8rem',
                        lineHeight: '1.5',
                        color: '#d4d4d4',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}>
                        {sitemapContent}
                      </pre>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* SEO Checklist Section */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              border: '1px solid #e8e8e8',
              marginTop: '24px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1A1A1A', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
                  <CheckSquare size={20} />
                  Monthly SEO Checklist
                </h3>
                <button
                  onClick={resetChecklist}
                  style={{
                    padding: '8px 16px',
                    background: '#f8f9fa',
                    border: '1px solid #e8e8e8',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '0.85rem',
                    color: '#666'
                  }}
                >
                  <RotateCcw size={14} />
                  Reset All
                </button>
              </div>

              {loadingChecklist ? (
                <p style={{ color: '#888' }}>Loading checklist...</p>
              ) : seoChecklist ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
                  {/* Weekly Tasks */}
                  <div>
                    <h4 style={{ fontSize: '0.95rem', fontWeight: '600', color: '#C8A860', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Weekly
                    </h4>
                    {seoChecklist.items.weekly.map((item) => (
                      <label
                        key={item.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px',
                          background: item.completed ? '#e8f5e9' : '#f8f9fa',
                          borderRadius: '8px',
                          marginBottom: '8px',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        <button
                          onClick={() => toggleChecklistItem(item.id, item.completed)}
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                        >
                          {item.completed ? (
                            <CheckSquare size={22} color="#2e7d32" />
                          ) : (
                            <Square size={22} color="#999" />
                          )}
                        </button>
                        <span style={{
                          color: item.completed ? '#2e7d32' : '#333',
                          textDecoration: item.completed ? 'line-through' : 'none',
                          fontSize: '0.9rem'
                        }}>
                          {item.label}
                        </span>
                      </label>
                    ))}
                  </div>

                  {/* Monthly Tasks */}
                  <div>
                    <h4 style={{ fontSize: '0.95rem', fontWeight: '600', color: '#C8A860', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Monthly
                    </h4>
                    {seoChecklist.items.monthly.map((item) => (
                      <label
                        key={item.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px',
                          background: item.completed ? '#e8f5e9' : '#f8f9fa',
                          borderRadius: '8px',
                          marginBottom: '8px',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        <button
                          onClick={() => toggleChecklistItem(item.id, item.completed)}
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                        >
                          {item.completed ? (
                            <CheckSquare size={22} color="#2e7d32" />
                          ) : (
                            <Square size={22} color="#999" />
                          )}
                        </button>
                        <span style={{
                          color: item.completed ? '#2e7d32' : '#333',
                          textDecoration: item.completed ? 'line-through' : 'none',
                          fontSize: '0.9rem'
                        }}>
                          {item.label}
                        </span>
                      </label>
                    ))}
                  </div>

                  {/* Quarterly Tasks */}
                  <div>
                    <h4 style={{ fontSize: '0.95rem', fontWeight: '600', color: '#C8A860', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Quarterly
                    </h4>
                    {seoChecklist.items.quarterly.map((item) => (
                      <label
                        key={item.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px',
                          background: item.completed ? '#e8f5e9' : '#f8f9fa',
                          borderRadius: '8px',
                          marginBottom: '8px',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        <button
                          onClick={() => toggleChecklistItem(item.id, item.completed)}
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                        >
                          {item.completed ? (
                            <CheckSquare size={22} color="#2e7d32" />
                          ) : (
                            <Square size={22} color="#999" />
                          )}
                        </button>
                        <span style={{
                          color: item.completed ? '#2e7d32' : '#333',
                          textDecoration: item.completed ? 'line-through' : 'none',
                          fontSize: '0.9rem'
                        }}>
                          {item.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ) : (
                <p style={{ color: '#888' }}>Unable to load checklist</p>
              )}
            </div>

            {/* Guide Content Modal */}
            {selectedGuide && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '20px'
              }}>
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  maxWidth: '900px',
                  width: '100%',
                  maxHeight: '90vh',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <div style={{
                    padding: '20px 24px',
                    borderBottom: '1px solid #e8e8e8',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <h3 style={{ margin: 0, color: '#1A1A1A' }}>
                      {guides.find(g => g.id === selectedGuide)?.title || 'Guide'}
                    </h3>
                    <button
                      onClick={() => { setSelectedGuide(null); setGuideContent(''); }}
                      style={{
                        background: '#f8f9fa',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px',
                        cursor: 'pointer'
                      }}
                    >
                      <X size={20} />
                    </button>
                  </div>
                  <div style={{
                    padding: '24px',
                    overflow: 'auto',
                    flex: 1
                  }}>
                    {loadingGuide ? (
                      <p style={{ color: '#888' }}>Loading guide...</p>
                    ) : (
                      <pre style={{
                        whiteSpace: 'pre-wrap',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                        fontSize: '0.9rem',
                        lineHeight: '1.7',
                        color: '#333',
                        margin: 0
                      }}>
                        {guideContent}
                      </pre>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
